/**
 * Deal の現状から「次に何をすべきか」を導出する hook。
 * 商談詳細ページの上部「次のアクション」バーで利用。
 *
 * 設計:
 *  - Deal / Briefing / Quote / Invoice / PurchaseOrder / Delivery を読み取り
 *  - 状態に応じて優先度付きアクション配列を返す
 *  - 各アクションは tab key (どのタブを開けばよいか) を持ち、UI 側でジャンプを実装
 */
import type { Deal } from '@goenhub/shared';
import { countDeliverySerials, inspectionProgress } from '@goenhub/shared';
import { useMemo } from 'react';

import { useBriefingRecords } from '@/hooks/useBriefings';
import { type DealRecord } from '@/hooks/useDeals';
import { useProductCategories } from '@/hooks/useProductCategories';
import { useDealQuotes } from '@/hooks/useQuotes';
import { useDealDeliveries, useDealInvoices, useDealPurchaseOrders } from '@/hooks/useSalesOps';

export type DealTabKey = 'overview' | 'briefing' | 'quotes' | 'fulfillment' | 'consent' | 'contact';

export type ActionPriority = 'critical' | 'normal' | 'info';

export interface NextAction {
  key: string;
  /** 表示する文言 (アイコン込みは UI 側) */
  label: string;
  /** 補足説明 */
  hint?: string;
  /** クリック時に開くタブ */
  targetTab: DealTabKey;
  /** 指定時は、タブを開いたうえでこの発注カードまでスクロールする */
  targetPoId?: string;
  /** 緊急度 */
  priority: ActionPriority;
  /** 絵文字アイコン (シンプルに識別) */
  emoji: string;
}

interface UseNextActionsResult {
  actions: NextAction[];
  /** 各タブに付与するバッジ件数 (Deal の未処理アクション数) */
  badgeCounts: Record<DealTabKey, number>;
  isLoading: boolean;
}

export function useNextActions(deal: DealRecord | null | undefined): UseNextActionsResult {
  const dealId = deal?.id ?? null;
  const briefingRecords = useBriefingRecords(dealId);
  const quotes = useDealQuotes(dealId);
  const invoices = useDealInvoices(dealId);
  const purchaseOrders = useDealPurchaseOrders(dealId);
  const deliveries = useDealDeliveries(dealId);
  const { data: categories } = useProductCategories({ includeInactive: true });

  const isLoading =
    briefingRecords.isLoading ||
    quotes.isLoading ||
    invoices.isLoading ||
    purchaseOrders.isLoading ||
    deliveries.isLoading;

  const actions = useMemo<NextAction[]>(() => {
    if (!deal) return [];
    const out: NextAction[] = [];

    const hasBriefingSchedule = !!deal.scheduledBriefingAt;
    const hasBriefingRecord = (briefingRecords.data ?? []).length > 0;
    const quoteList = quotes.data ?? [];
    const latestQuote = quoteList[0]; // createdAt desc 想定
    const hasQuote = quoteList.length > 0;
    const quoteSent = quoteList.some((q) => q.status === 'sent');
    const invoiceList = invoices.data ?? [];
    const unpaidInvoice = invoiceList.find((i) => i.status === 'unpaid');
    const overdueInvoice = invoiceList.find((i) => i.status === 'overdue');
    const poList = purchaseOrders.data ?? [];
    const pendingPO = poList.find((p) => p.status === 'pending');
    // TimeWaver: 送金依頼済みなのに最終インボイスが未登録の発注
    const isTimeWaver =
      !!deal.primaryCategoryId &&
      (categories ?? []).some((c) => c.id === deal.primaryCategoryId && c.name === 'TimeWaver');
    const poMissingFinalInvoice = isTimeWaver
      ? poList.find(
          (p) => p.status !== 'cancelled' && !!p.transferRequestedAt && !p.officialOrderPdfPath,
        )
      : undefined;
    const deliveryList = deliveries.data ?? [];
    const incompleteDelivery = deliveryList.find(
      (d) => d.status !== 'completed' && d.status !== 'cancelled',
    );
    const incompleteDeliveryWithMissingSerials = deliveryList.find((d) => {
      if (d.status === 'completed' || d.status === 'cancelled') return false;
      // 台数基準で数える (シリアル欄が空配列の発送も未入力として拾う)
      return countDeliverySerials(d.lineItems).missing > 0;
    });

    // closed (won/lost) は表示なし → 「履歴を確認」のみ
    if (deal.status === 'closed' && (deal.stage === 'won' || deal.stage === 'lost')) {
      out.push({
        key: 'closed_done',
        label: deal.stage === 'won' ? '成約済み・後続工程に進めます' : '失注確定済み',
        hint: deal.stage === 'won' ? '請求/発注/発送タブで進行を確認してください' : undefined,
        targetTab: deal.stage === 'won' ? 'fulfillment' : 'overview',
        priority: 'info',
        emoji: deal.stage === 'won' ? '🎉' : '🪦',
      });
      // won 後の追加ガイダンスを以下にも続ける
    }

    // ─── Stage ベースの主アクション ───
    if (deal.stage === 'document_requested' && !hasBriefingSchedule) {
      out.push({
        key: 'schedule_briefing',
        label: '個別説明の日程を確定する',
        hint: '顧客から候補日が選ばれていれば確定、未選択なら日程を提案する',
        targetTab: 'briefing',
        priority: 'critical',
        emoji: '📅',
      });
    }

    if (deal.stage === 'briefing' && !hasBriefingRecord) {
      out.push({
        key: 'record_briefing',
        label: '個別説明の結果を記録する',
        hint: '会話内容・関心商品・成約レベルを記録',
        targetTab: 'briefing',
        priority: 'critical',
        emoji: '📝',
      });
    }

    if (deal.stage === 'briefing' && hasBriefingRecord && !hasQuote) {
      out.push({
        key: 'create_quote',
        label: '見積を作成する',
        hint: '個別説明記録から内容をスナップショット',
        targetTab: 'quotes',
        priority: 'critical',
        emoji: '💰',
      });
    }

    if (deal.stage === 'quote' && hasQuote && latestQuote && !quoteSent) {
      out.push({
        key: 'send_quote',
        label: '見積メールを送信する',
        hint: `${latestQuote.title || '見積書'} を顧客に送る`,
        targetTab: 'quotes',
        priority: 'critical',
        emoji: '✉',
      });
    }

    if (deal.stage === 'quote' && quoteSent && deal.status === 'open') {
      // 14日以上経過していれば成約/失注確定を促す
      const sentAt = quoteList.find((q) => q.status === 'sent')?.sentAt;
      const sentMs = tsToMs(sentAt);
      if (sentMs && Date.now() - sentMs > 14 * 24 * 60 * 60 * 1000) {
        out.push({
          key: 'confirm_won_lost',
          label: '成約 / 失注を確定する',
          hint: '見積送信から14日経過しています。判断を確定してください',
          targetTab: 'overview',
          priority: 'normal',
          emoji: '🎯',
        });
      }
    }

    // 入金関連
    if (unpaidInvoice) {
      out.push({
        key: 'mark_paid',
        label: '入金を確認する',
        hint: `請求 ${unpaidInvoice.invoiceNumber} が未入金です`,
        targetTab: 'fulfillment',
        priority: overdueInvoice ? 'critical' : 'normal',
        emoji: '💴',
      });
    }
    if (overdueInvoice) {
      out.push({
        key: 'overdue',
        label: '支払期限超過の請求あり',
        hint: `${overdueInvoice.invoiceNumber}: 督促を検討してください`,
        targetTab: 'fulfillment',
        priority: 'critical',
        emoji: '⚠',
      });
    }

    // 発注関連
    if (pendingPO) {
      out.push({
        key: 'order_po',
        label: '仕入先に発注する',
        hint: `発注 ${pendingPO.poNumber} が未発注です`,
        targetTab: 'fulfillment',
        targetPoId: pendingPO.id,
        priority: 'normal',
        emoji: '📦',
      });
    }
    if (poMissingFinalInvoice) {
      out.push({
        key: 'upload_final_invoice',
        label: '最終インボイスを登録する',
        hint: `発注 ${poMissingFinalInvoice.poNumber}: 送金依頼済み・最終インボイスが未登録です`,
        targetTab: 'fulfillment',
        targetPoId: poMissingFinalInvoice.id,
        priority: 'normal',
        emoji: '📄',
      });
    }

    // 入荷・検品 → 顧客へ発送 (2026-10-30〜)
    const poAwaitingShipment = poList.find((p) => p.status === 'inspected');
    const poAwaitingInspection = poList.find((p) => p.status === 'received');
    const poAwaitingArrival = poList.find((p) => p.status === 'ordered');
    if (poAwaitingShipment) {
      out.push({
        key: 'ship_po',
        label: '顧客へ発送する',
        hint: `発注 ${poAwaitingShipment.poNumber}: 検品済・シリアルがそろっています`,
        targetTab: 'fulfillment',
        targetPoId: poAwaitingShipment.id,
        priority: 'critical',
        emoji: '🚚',
      });
    }
    if (poAwaitingInspection) {
      const pr = inspectionProgress(poAwaitingInspection, isTimeWaver);
      out.push({
        key: 'inspect_po',
        label: '検品・シリアルを入力する',
        hint: `発注 ${poAwaitingInspection.poNumber}: 入荷済 · ${
          isTimeWaver
            ? `そろった機器 ${pr.completeUnits}/${pr.totalUnits}`
            : `シリアル ${pr.filledSerials}/${pr.totalUnits}`
        }`,
        targetTab: 'fulfillment',
        targetPoId: poAwaitingInspection.id,
        priority: 'critical',
        emoji: '🔢',
      });
    }
    if (poAwaitingArrival) {
      out.push({
        key: 'record_arrival',
        label: '入荷を記録する',
        hint: `発注 ${poAwaitingArrival.poNumber}: 商品が弊社に届いたら記録します`,
        targetTab: 'fulfillment',
        targetPoId: poAwaitingArrival.id,
        priority: 'normal',
        emoji: '📦',
      });
    }

    // 発送・シリアル
    if (incompleteDeliveryWithMissingSerials) {
      const { missing, total } = countDeliverySerials(
        incompleteDeliveryWithMissingSerials.lineItems,
      );
      out.push({
        key: 'enter_serials',
        label: `シリアル ${total - missing}/${total} → 入力する`,
        hint: '納品完了で稼働機器が自動登録されます',
        targetTab: 'fulfillment',
        priority: 'critical',
        emoji: '🔢',
      });
    } else if (incompleteDelivery) {
      out.push({
        key: 'complete_delivery',
        label: '納品完了処理を行う',
        hint: `発送 ${incompleteDelivery.deliveryNumber} が ${incompleteDelivery.status}`,
        targetTab: 'fulfillment',
        priority: 'normal',
        emoji: '🚚',
      });
    }

    // すべて完了
    if (out.length === 0 && deal.status === 'open') {
      out.push({
        key: 'all_clear',
        label: '次のアクションは特にありません',
        hint: '新規対応が発生したらここに表示されます',
        targetTab: 'overview',
        priority: 'info',
        emoji: '✨',
      });
    }

    return out;
  }, [
    deal,
    briefingRecords.data,
    quotes.data,
    invoices.data,
    purchaseOrders.data,
    deliveries.data,
    categories,
  ]);

  const badgeCounts = useMemo<Record<DealTabKey, number>>(() => {
    const counts: Record<DealTabKey, number> = {
      overview: 0,
      briefing: 0,
      quotes: 0,
      fulfillment: 0,
      consent: 0,
      contact: 0,
    };
    for (const a of actions) {
      if (a.priority === 'info') continue;
      counts[a.targetTab] += 1;
    }
    return counts;
  }, [actions]);

  return { actions, badgeCounts, isLoading };
}

// ─────────────────────────────────────────────
// ヘルパ
// ─────────────────────────────────────────────

function tsToMs(v: Deal['scheduledBriefingAt']): number | null {
  if (!v) return null;
  if (typeof v === 'string') {
    const ms = Date.parse(v);
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof v === 'object' && 'toMillis' in v) {
    try {
      return (v as { toMillis: () => number }).toMillis();
    } catch {
      return null;
    }
  }
  return null;
}
